import React, { useState } from "react";
import { Link } from "react-router-dom";
import { API, formatApiError } from "../lib/api";
import { Heart, FileText, MessageCircle, Upload, X, Copy, CheckCircle2, FileDown, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

const CATEGORIES = [
  { value: "cooperado", label: "Cooperado(a)" },
  { value: "nao_cooperado", label: "Não Cooperado(a)" },
  { value: "comunidade", label: "Comunidade" },
  { value: "instituicao", label: "Instituição" },
];

const SOCIAL_WHATSAPP = "5593991493368";
const SOCIAL_NAME = "Elen Samara";

const STATUS_LABEL_MAP = {
  recebida: "Recebida", em_analise: "Em análise", aprovada: "Aprovada",
  indeferida: "Indeferida", concluida: "Concluída",
};

function formatDocBR(v) {
  const c = (v || "").replace(/\D/g, "").slice(0, 14);
  if (c.length <= 11) {
    // CPF
    if (c.length <= 3) return c;
    if (c.length <= 6) return `${c.slice(0, 3)}.${c.slice(3)}`;
    if (c.length <= 9) return `${c.slice(0, 3)}.${c.slice(3, 6)}.${c.slice(6)}`;
    return `${c.slice(0, 3)}.${c.slice(3, 6)}.${c.slice(6, 9)}-${c.slice(9)}`;
  }
  // CNPJ
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
}

function formatPhoneBR(v) {
  const p = (v || "").replace(/\D/g, "").slice(0, 11);
  if (p.length <= 2) return p;
  if (p.length <= 7) return `(${p.slice(0, 2)}) ${p.slice(2)}`;
  return `(${p.slice(0, 2)}) ${p.slice(2, 7)}-${p.slice(7)}`;
}

const INIT = {
  name: "", cpf: "", phone: "", community: "",
  category: "cooperado", request_type: "", description: "",
  institution: "", address: "", email: "", is_cooperado_choice: "",
  accepts_terms: false, accepts_data: false,
};

export default function SocialRequest() {
  const [form, setForm] = useState(INIT);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (e) => {
    const list = Array.from(e.target.files || []);
    const allowed = list.filter((f) => f.size <= 5 * 1024 * 1024);
    if (list.length !== allowed.length) toast.error("Cada arquivo deve ter no máximo 5MB.");
    setFiles((curr) => [...curr, ...allowed].slice(0, 5));
  };
  const removeFile = (idx) => setFiles((arr) => arr.filter((_, i) => i !== idx));

  const buildFormData = (includeFiles = true) => {
    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("cpf", form.cpf.replace(/\D/g, ""));
    fd.append("phone", form.phone.replace(/\D/g, ""));
    fd.append("community", form.community);
    fd.append("category", form.category);
    fd.append("request_type", form.request_type);
    fd.append("description", form.description);
    fd.append("institution", form.institution);
    fd.append("address", form.address);
    fd.append("email", form.email);
    fd.append("is_cooperado_choice", form.is_cooperado_choice);
    fd.append("accepts_terms", form.accepts_terms ? "true" : "false");
    fd.append("accepts_data", form.accepts_data ? "true" : "false");
    if (includeFiles) files.forEach((f) => fd.append("attachments", f, f.name));
    return fd;
  };

  const validate = () => {
    const doc = form.cpf.replace(/\D/g, "");
    if (![11, 14].includes(doc.length)) { toast.error("CPF (11 dígitos) ou CNPJ (14 dígitos)."); return false; }
    if (form.description.trim().length < 10) { toast.error("Descreva sua demanda com mais detalhes."); return false; }
    return true;
  };

  const generatePreview = async () => {
    if (!form.name || !form.cpf || !form.phone) {
      toast.error("Preencha pelo menos nome, CPF/CNPJ e telefone antes de gerar o PDF.");
      return;
    }
    setPreviewing(true);
    try {
      const res = await fetch(`${API}/social/preview-pdf`, { method: "POST", body: buildFormData(false) });
      if (!res.ok) throw new Error("Erro ao gerar prévia");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "previa_termo_coomflona.pdf";
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      toast.success("Prévia gerada — confira antes de enviar.");
    } catch (err) { toast.error(err.message); } finally { setPreviewing(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accepts_terms) { toast.error("Você precisa marcar: \"Li e concordo com os termos\"."); return; }
    if (!form.accepts_data) { toast.error("Você precisa autorizar o tratamento dos dados."); return; }
    if (!validate()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/social/requests`, { method: "POST", body: buildFormData(true) });
      if (!res.ok) {
        const j = await res.json().catch(() => ({ detail: "Erro" }));
        throw new Error(typeof j.detail === "string" ? j.detail : formatApiError({ response: { data: j } }));
      }
      const data = await res.json();
      setResult(data);
      toast.success("Solicitação registrada!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(err.message || "Falha ao enviar"); } finally { setSending(false); }
  };

  if (result) {
    const sigUrl = `${window.location.origin}/setor-social/assinar/${result.signature_token}`;
    const categoryLabel = CATEGORIES.find((c) => c.value === result.category)?.label || result.category;
    const statusLabel = STATUS_LABEL_MAP[result.status] || "Recebida";
    const waMsg =
      `COOMFLONA - Confirmação de Solicitação%0A%0A` +
      `Olá! Sua solicitação foi registrada com sucesso.%0A%0A` +
      `Protocolo: ${result.protocol}%0A` +
      `Nome: ${result.name}%0A` +
      `Contato: ${result.phone_display}%0A` +
      `Categoria: ${categoryLabel}%0A` +
      `Situação: ${statusLabel}%0A%0A` +
      `Para assinar digitalmente o termo (no celular):%0A${sigUrl}%0A%0A` +
      `Em caso de dúvidas:%0A${SOCIAL_NAME}%0ASetor Social - COOMFLONA%0AWhatsApp: (93) 99149-3368`;
    const waLinkUser = `https://wa.me/${result.phone}?text=${waMsg}`;
    const waLinkResp = `https://wa.me/${SOCIAL_WHATSAPP}?text=${waMsg}`;

    return (
      <div className="container mx-auto px-6 py-16 max-w-3xl" data-testid="social-success">
        <div className="bg-cream-100 border border-forest-700 p-8">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 className="w-7 h-7 text-forest-700" />
            <h1 className="font-display text-3xl text-forest-900">Solicitação registrada!</h1>
          </div>
          <p className="text-forest-900/75 mt-2">Olá! Sua solicitação foi registrada com sucesso.</p>

          <div className="mt-6 bg-white border border-cream-400 p-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-forest-700">Protocolo</div>
            <div className="font-mono text-2xl text-forest-900 mt-1" data-testid="success-protocol">{result.protocol}</div>
            <button type="button" onClick={() => { navigator.clipboard.writeText(result.protocol); toast.success("Protocolo copiado!"); }}
              className="mt-2 inline-flex items-center gap-1 text-xs text-forest-700 hover:text-copaiba-700">
              <Copy className="w-3.5 h-3.5" /> Copiar protocolo
            </button>
          </div>

          <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-forest-700/65 text-xs uppercase tracking-[0.14em]">Nome</span><div className="text-forest-900">{result.name}</div></div>
            <div><span className="text-forest-700/65 text-xs uppercase tracking-[0.14em]">Contato</span><div className="text-forest-900">{result.phone_display}</div></div>
            <div><span className="text-forest-700/65 text-xs uppercase tracking-[0.14em]">Categoria</span><div className="text-forest-900">{categoryLabel}</div></div>
            <div><span className="text-forest-700/65 text-xs uppercase tracking-[0.14em]">Situação</span><div className="text-forest-900">{statusLabel}</div></div>
          </div>

          <div className="mt-6 bg-copaiba-100 border border-copaiba-300 p-5">
            <div className="font-display text-xl text-forest-900 mb-2">Próximo passo: assinar o termo</div>
            <p className="text-sm text-forest-900/80 mb-3">Abra o link no seu celular e assine com o dedo:</p>
            <a href={sigUrl} target="_blank" rel="noreferrer" className="font-mono text-xs text-forest-700 break-all hover:text-copaiba-700">{sigUrl}</a>
            <div className="flex flex-wrap gap-2 mt-4">
              <a href={waLinkUser} target="_blank" rel="noreferrer" data-testid="success-wa-user" className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white hover:opacity-90 text-sm">
                <MessageCircle className="w-4 h-4" /> Enviar pelo WhatsApp para mim
              </a>
              <a href={waLinkResp} target="_blank" rel="noreferrer" data-testid="success-wa-elem" className="inline-flex items-center gap-2 px-4 py-2 bg-forest-700 text-cream-100 hover:bg-forest-900 text-sm">
                <MessageCircle className="w-4 h-4" /> Avisar a {SOCIAL_NAME}
              </a>
            </div>
          </div>

          <div className="mt-6 text-xs text-forest-900/65">
            Para acompanhar sua solicitação entre em contato com:<br />
            <strong>{SOCIAL_NAME}</strong> — Setor Social - COOMFLONA · WhatsApp: (93) 99149-3368
          </div>

          <div className="mt-6 flex gap-3">
            <Button onClick={() => { setResult(null); setFiles([]); setForm(INIT); }}
              className="rounded-none bg-forest-700 hover:bg-forest-900 text-cream-100" data-testid="new-request-button">
              Nova solicitação
            </Button>
            <Link to="/" className="px-5 py-2 border border-cream-400 text-forest-900 hover:bg-cream-200 inline-flex items-center">Voltar ao site</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-16 max-w-3xl" data-testid="social-request-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center">
          <Heart className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest-700">Setor Social</div>
          <h1 className="font-display text-4xl text-forest-900">Solicitação — Setor Social COOMFLONA</h1>
        </div>
      </div>
      <p className="text-forest-900/75 mb-8 leading-relaxed">
        Prezado cooperado, comunidade ou instituição: acesse o documento abaixo e preencha a solicitação para que
        possamos registrar o atendimento. Após o envio você receberá um <strong>número de protocolo</strong> automático.
      </p>

      {/* Declaração e Termo de Aceite (sempre visível) */}
      <details className="bg-cream-100 border border-forest-700/30 mb-6" open data-testid="declaracao-block">
        <summary className="cursor-pointer list-none flex items-center justify-between px-5 py-4 hover:bg-cream-200/60">
          <span className="font-display text-lg text-forest-900 inline-flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-forest-700" /> Ver declaração e termo de aceite
          </span>
          <span className="text-xs uppercase tracking-[0.18em] text-forest-700">clique para abrir/fechar</span>
        </summary>
        <div className="px-5 pb-5 text-sm text-forest-900/85 leading-relaxed space-y-3 border-t border-cream-400">
          <div className="font-display text-base text-forest-900 pt-4">DECLARAÇÃO E TERMO DE ACEITE</div>
          <p>Declaro que as informações prestadas são verdadeiras e assumo total responsabilidade por sua veracidade.</p>
          <p>Estou ciente de que o envio desta solicitação <strong>não gera direito adquirido</strong> à concessão de qualquer benefício, apoio, patrocínio ou doação, ficando a aprovação condicionada à análise técnica, financeira, administrativa e estatutária da COOMFLONA.</p>
          <p>Autorizo a COOMFLONA a utilizar os dados fornecidos exclusivamente para fins de análise, registro, auditoria, prestação de contas e demais procedimentos relacionados à presente solicitação, observando a legislação aplicável.</p>
          <p>Comprometo-me a prestar contas da utilização dos recursos ou benefícios eventualmente concedidos, quando solicitado pela COOMFLONA.</p>
        </div>
      </details>

      <form onSubmit={submit} className="bg-white border border-cream-400 p-8 space-y-5" data-testid="social-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nome do Solicitante *">
            <input required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Digite seu nome completo" data-testid="social-name" className="inp" />
          </Field>
          <Field label="CPF ou CNPJ do Solicitante *">
            <input required value={form.cpf} onChange={(e) => update("cpf", formatDocBR(e.target.value))} placeholder="Digite seu CPF ou CNPJ" data-testid="social-cpf" className="inp" />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="É cooperado?">
            <select value={form.is_cooperado_choice} onChange={(e) => update("is_cooperado_choice", e.target.value)} data-testid="social-iscoop" className="inp">
              <option value="">Selecione...</option>
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
          </Field>
          <Field label="Categoria *">
            <select required value={form.category} onChange={(e) => update("category", e.target.value)} data-testid="social-category" className="inp">
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Instituição">
          <input value={form.institution} onChange={(e) => update("institution", e.target.value)} placeholder="Digite o nome da instituição (se aplicável)" data-testid="social-institution" className="inp" />
        </Field>

        <Field label="Endereço">
          <input value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Digite o endereço completo" data-testid="social-address" className="inp" />
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="E-mail">
            <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="Digite seu e-mail" data-testid="social-email" className="inp" />
          </Field>
          <Field label="Telefone / WhatsApp *">
            <input required value={form.phone} onChange={(e) => update("phone", formatPhoneBR(e.target.value))} placeholder="(93) 99999-9999" data-testid="social-phone" className="inp" />
          </Field>
        </div>

        <Field label="Comunidade">
          <input value={form.community} onChange={(e) => update("community", e.target.value)} placeholder="Ex.: Maguari, Jamaraquá, Tauari..." data-testid="social-community" className="inp" />
        </Field>

        <Field label="Tipo de Solicitação">
          <input value={form.request_type} onChange={(e) => update("request_type", e.target.value)} placeholder="Ex.: Apoio para deslocamento médico, doação, parceria..." data-testid="social-type" className="inp" />
        </Field>

        <Field label="Descrição da solicitação *">
          <textarea required rows={6} value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Explique o motivo da solicitação com detalhes." data-testid="social-description" className="inp resize-y" />
        </Field>

        <Field label="Anexos (PDF ou imagem · até 5MB cada · até 5 arquivos)">
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-dashed border-cream-400 hover:border-forest-700 hover:bg-cream-200 text-forest-900 text-sm">
            <Upload className="w-4 h-4" /> Adicionar arquivos
            <input type="file" multiple onChange={onFile} accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="social-files" className="hidden" />
          </label>
          {files.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm">
              {files.map((f, i) => (
                <li key={i} className="flex items-center justify-between bg-cream-100 border border-cream-400 px-3 py-2">
                  <span className="flex items-center gap-2 text-forest-900"><FileText className="w-4 h-4 text-forest-700" /> {f.name} <span className="text-xs text-forest-900/55">({(f.size / 1024).toFixed(0)} KB)</span></span>
                  <button type="button" onClick={() => removeFile(i)} className="text-terracotta-500 hover:text-terracotta-700" data-testid={`remove-file-${i}`}><X className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </Field>

        {/* CONCORDÂNCIA — checkboxes obrigatórios */}
        <div className="bg-cream-200 border border-cream-400 p-4 space-y-3">
          <div className="text-xs uppercase tracking-[0.18em] text-forest-700 font-medium">CONCORDÂNCIA *</div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={form.accepts_terms} onChange={(e) => update("accepts_terms", e.target.checked)} data-testid="accept-terms" className="mt-1 w-4 h-4 accent-forest-700" />
            <span className="text-sm text-forest-900">Li e <strong>concordo integralmente</strong> com os termos acima.</span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={form.accepts_data} onChange={(e) => update("accepts_data", e.target.checked)} data-testid="accept-data" className="mt-1 w-4 h-4 accent-forest-700" />
            <span className="text-sm text-forest-900">Autorizo o <strong>tratamento dos meus dados</strong> para análise desta solicitação.</span>
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button type="submit" disabled={sending} data-testid="social-submit" className="flex-1 bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none py-6 text-base">
            {sending ? "Enviando..." : "Enviar solicitação"}
          </Button>
          <Button type="button" onClick={generatePreview} disabled={previewing} data-testid="social-preview-pdf" className="sm:w-auto bg-white text-forest-900 border border-cream-400 hover:bg-cream-200 rounded-none py-6 text-base">
            {previewing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando...</>) : (<><FileDown className="w-4 h-4 mr-2" /> Gerar PDF (prévia)</>)}
          </Button>
        </div>

        <p className="text-xs text-forest-900/65 text-center">
          Após o envio, será gerado um protocolo automático. Guarde-o para acompanhar o atendimento.
        </p>
      </form>

      {/* Rodapé */}
      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        <a href={`https://wa.me/${SOCIAL_WHATSAPP}`} target="_blank" rel="noreferrer"
           className="flex items-center gap-3 bg-[#25D366] text-white px-5 py-3 hover:opacity-90"
           data-testid="footer-wa-social">
          <MessageCircle className="w-5 h-5" />
          <div className="text-sm">
            <div className="text-white/85 text-[10px] uppercase tracking-[0.18em]">WhatsApp Setor Social COOMFLONA</div>
            <div className="font-medium">+55 93 99149-3368</div>
          </div>
        </a>
        <div className="flex items-center gap-3 bg-cream-200 border border-cream-400 px-5 py-3">
          <div className="w-9 h-9 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center"><Heart className="w-4 h-4" /></div>
          <div className="text-sm">
            <div className="text-forest-900/65 text-[10px] uppercase tracking-[0.18em]">Responsável</div>
            <div className="font-medium text-forest-900">ELEN SAMARA</div>
          </div>
        </div>
      </div>

      <div className="mt-4 text-center">
        <Link to="/setor-social/login" className="text-sm text-forest-700 hover:text-copaiba-700 underline underline-offset-2" data-testid="link-admin-access">
          Acesso administração / protocolos
        </Link>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

// CSS helper para inputs/selects
if (typeof document !== "undefined" && !document.getElementById("inp-style")) {
  const s = document.createElement("style");
  s.id = "inp-style";
  s.innerHTML = `.inp{display:block;width:100%;background:#FDFAF1;border:1px solid #E8DFCB;padding:.75rem;color:#1A3620;font-size:.95rem;outline:none}
.inp:focus{border-color:#1A3620}
select.inp{appearance:none;background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231A3620' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>");background-repeat:no-repeat;background-position:right .75rem center;background-size:1rem;padding-right:2rem}`;
  document.head.appendChild(s);
}
